import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck, Sparkles } from 'lucide-react';
import { ISO_2022_THEMES, MATURITY_LEVELS } from '@/lib/calculationEngine';
import { LabeledSlider } from './fields/LabeledSlider';

function MaturityBadge({ level }) {
  const { t } = useTranslation();
  const label = MATURITY_LEVELS[level]?.label;
  return (
    <span
      className="text-xs text-[#527F19]"
      style={{ fontFamily: 'IBM Plex Mono, monospace' }}
    >
      {level} &mdash; {label ? t(`maturity.${label.toLowerCase()}`) : '-'}
    </span>
  );
}

// Derived gap status from a 1-5 maturity score (tangible gap-analysis view).
function gapStatusKey(level) {
  if (level <= 1) return 'notStarted';
  if (level <= 3) return 'partial';
  return 'implemented';
}
const GAP_STATUS_CLASS = {
  notStarted: 'bg-red-50 text-red-600 border-red-200',
  partial: 'bg-[#B7791F]/[0.06] text-[#B7791F] border-[#B7791F]/25',
  implemented: 'bg-[#F1F8E8] text-[#527F19] border-[#527F19]/25',
};

function GapStatusBadge({ level }) {
  const { t } = useTranslation();
  const key = gapStatusKey(level);
  return (
    <Badge
      variant="outline"
      className={`h-4 px-1.5 text-[9px] font-semibold uppercase tracking-wide ${GAP_STATUS_CLASS[key]}`}
      data-testid={`gap-status-${key}`}
    >
      {t('iso2022.gapStatus.' + key)}
    </Badge>
  );
}

// Average maturity (default 3) across a theme's control groups → 0-100% for the rollup badge.
function themeAverage(theme, domainMaturity = {}) {
  const vals = theme.groups.map(g => domainMaturity[g.id] || 3);
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

function ControlGroupRow({ group, value, label, onChange, newLabel, newControlText }) {
  const groupLabel = (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className="text-xs font-medium text-[#171A1D]">{label}</span>
      <span className="text-[10px] font-mono text-[#8A929B]">{group.code}</span>
      {group.isNew && (
        <Badge
          variant="outline"
          title={newControlText}
          className="h-4 px-1.5 text-[9px] font-semibold uppercase tracking-wide border-[#2867B2]/35 text-[#2867B2] bg-[#2867B2]/[0.06]"
          data-testid={`new-2022-badge-${group.id}`}
        >
          <Sparkles className="w-2.5 h-2.5 mr-0.5" /> {newLabel}
        </Badge>
      )}
    </span>
  );
  return (
    <LabeledSlider
      label={groupLabel}
      value={value}
      onValueChange={v => onChange(group.id, v)}
      min={1}
      max={5}
      step={1}
      display={
        <span className="flex items-center gap-2">
          <GapStatusBadge level={value} />
          <MaturityBadge level={value} />
        </span>
      }
      ariaLabel={`${label} (${group.code}): ${value}`}
      testId={`group-${group.id}-slider`}
      sliderClassName="mt-1.5"
    />
  );
}

export function SecurityMaturity({ config, updateConfig }) {
  const { t } = useTranslation();
  const domainMaturity = config.domainMaturity || {};

  const handleGroupChange = (groupId, value) => {
    updateConfig({ domainMaturity: { ...domainMaturity, [groupId]: value } });
  };

  return (
    <Card className="bg-white border-[#DDE1E5]" data-testid="security-maturity-section">
      <CardHeader className="pb-4">
        <CardTitle
          className="flex items-center gap-2 text-lg text-[#171A1D]"
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        >
          <ShieldCheck className="w-5 h-5 text-[#66707A] shrink-0" />
          <span className="min-w-0 [overflow-wrap:anywhere]" data-testid="security-maturity-title">{t('maturity.title')}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={config.maturityMode} onValueChange={v => updateConfig({ maturityMode: v })}>
          <TabsList className="grid w-full grid-cols-2 bg-[#F5F6F7]">
            <TabsTrigger value="simple" data-testid="maturity-simple-tab">{t('maturity.simple')}</TabsTrigger>
            <TabsTrigger value="advanced" data-testid="maturity-advanced-tab">{t('maturity.advanced')}</TabsTrigger>
          </TabsList>

          <TabsContent value="simple" className="mt-4">
            <LabeledSlider
              label={t('maturity.overallLevel')}
              value={config.overallMaturity}
              onValueChange={v => updateConfig({ overallMaturity: v })}
              min={1}
              max={5}
              step={1}
              display={<MaturityBadge level={config.overallMaturity} />}
              testId="overall-maturity-slider"
              sliderClassName="mt-3"
              captions={[
                t('maturity.initial'),
                t('maturity.developing'),
                t('maturity.defined'),
                t('maturity.managed'),
                t('maturity.optimized'),
              ]}
              captionClassName="mt-1.5"
            />
          </TabsContent>

          <TabsContent value="advanced" className="mt-4">
            <div className="flex items-start justify-between gap-3 mb-3">
              <p className="text-xs text-[#8A929B]">{t('iso2022.advancedDesc')}</p>
              <Badge
                variant="outline"
                className="shrink-0 text-[10px] border-[#2867B2]/25 text-[#2867B2] bg-[#2867B2]/[0.06]"
                data-testid="iso-2022-badge"
              >
                ISO 27001:2022
              </Badge>
            </div>
            <ScrollArea className="h-[420px] pr-3">
              <Accordion type="multiple" defaultValue={['organizational']} className="space-y-2">
                {ISO_2022_THEMES.map(theme => {
                  const avg = themeAverage(theme, domainMaturity);
                  return (
                    <AccordionItem
                      key={theme.id}
                      value={theme.id}
                      className="border border-[#DDE1E5] rounded-lg bg-[#F5F6F7] px-3"
                      data-testid={`theme-${theme.id}`}
                    >
                      <AccordionTrigger className="hover:no-underline py-3">
                        <div className="flex flex-1 items-center justify-between pr-2">
                          <div className="text-left">
                            <span className="text-sm font-semibold text-[#171A1D]">
                              {t('iso2022.themes.' + theme.id)}
                            </span>
                            <span className="ml-2 text-[10px] font-mono text-[#8A929B]">
                              {theme.code} · {theme.controlCount} {t('iso2022.controls')}
                            </span>
                          </div>
                          <span
                            className="text-xs font-bold text-[#527F19]"
                            style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                            data-testid={`theme-${theme.id}-avg`}
                          >
                            {avg.toFixed(1)}/5
                          </span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pb-3">
                        <div className="space-y-4 pt-1">
                          {theme.groups.map(group => (
                            <ControlGroupRow
                              key={group.id}
                              group={group}
                              label={t('iso2022.groups.' + group.id)}
                              value={domainMaturity[group.id] || 3}
                              onChange={handleGroupChange}
                              newLabel={t('iso2022.new')}
                              newControlText={t('iso2022.newControls.' + group.id)}
                            />
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
